export interface MethodRow {
  code: string;
  amount: number;
  shipments: number;
}

export interface DestinationGroup {
  id: string | null;
  name: string | null;
  methods: MethodRow[];
  /**
   * Distinct shipment count across the whole destination. Taken from the
   * destination-level rows: a manifest whose lines span two management
   * methods appears in both per-method distinct counts, so summing those
   * double-counts it. Falls back to the per-method sum when no
   * destination-level row matches.
   */
  totalShipments: number;
  lastShipmentDate: string | null;
}

interface ByMethodItem {
  destinationFacilityId?: string | null;
  destinationFacilityName?: string | null;
  managementMethodCode?: string | null;
  vendorAmount?: number | null;
  eManifestAmount?: number | null;
  documentAmount?: number | null;
  shipmentCount?: number | null;
  lastShipmentDate?: string | null;
}

interface ByDestinationItem {
  destinationFacilityId?: string | null;
  destinationFacilityName?: string | null;
  shipmentCount?: number | null;
}

const destinationKey = (item: {
  destinationFacilityId?: string | null;
  destinationFacilityName?: string | null;
}) => `${item.destinationFacilityId}-${item.destinationFacilityName}`;

/**
 * Group per-(destination, management method) rows into one card per
 * destination, pairing each with its destination-level distinct shipment
 * count.
 */
export function buildDestinationCardGroups(params: {
  byMethodItems: readonly ByMethodItem[];
  byDestinationItems: readonly ByDestinationItem[];
}): DestinationGroup[] {
  const { byMethodItems, byDestinationItems } = params;

  const destinationShipmentCounts = new Map<string, number>(
    byDestinationItems.map((item) => [destinationKey(item), item.shipmentCount ?? 0])
  );

  const destinations: DestinationGroup[] = [];
  const byDestination = new Map<string, DestinationGroup>();
  for (const item of byMethodItems) {
    const key = destinationKey(item);
    let group = byDestination.get(key);
    if (!group) {
      group = {
        id: item.destinationFacilityId ?? null,
        name: item.destinationFacilityName ?? null,
        methods: [],
        totalShipments: 0,
        lastShipmentDate: null,
      };
      byDestination.set(key, group);
      destinations.push(group);
    }
    group.methods.push({
      code: item.managementMethodCode || "—",
      amount: Math.max(item.vendorAmount ?? 0, item.eManifestAmount ?? 0, item.documentAmount ?? 0),
      shipments: item.shipmentCount ?? 0,
    });
    // Most-recent shipment across this destination's methods.
    if (
      item.lastShipmentDate &&
      (!group.lastShipmentDate || item.lastShipmentDate > group.lastShipmentDate)
    ) {
      group.lastShipmentDate = item.lastShipmentDate;
    }
  }

  for (const [key, group] of byDestination) {
    group.totalShipments =
      destinationShipmentCounts.get(key) ??
      group.methods.reduce((sum, method) => sum + method.shipments, 0);
  }

  return destinations;
}
